import {
  useClientTokenManager,
  useCurrentClientManager,
  useFeatureFlags,
} from '@cko/dashboard-shared';
import { FeatureFlagId } from '../../config/shared/enums/featureFlagIds';

const ACTIVE_DASHBOARD_STATE = 'Active';
const ENTITY_ONBOARDING_STATES = ['Draft', 'Pending', 'RequirementsDue'];

export const ONBOARDING_ROLE = 'Account Application Only';

/**
 * Hook to check if the user is an Enterprise Merchant.
 *
 * For ease of development, the EnterpriseMerchantUserFlag will only
 * be enabled for testing. The flag will not be enabled in production.
 *
 * @returns boolean - whether the current user is an enterprise merchant.
 */
export const useIsOnboardingEnterpriseMerchant = () => {
  const { tokenProperties } = useClientTokenManager();

  const { getFeatureFlag } = useFeatureFlags();
  const isApplierRoleEnabled = getFeatureFlag(FeatureFlagId.REMOVE_APPLIER_ROLE);

  const { client, entity: selectedEntity } = useCurrentClientManager();

  // If no entity is selected, default to the first Active entity in the list,
  // otherwise fallback to the first entity in the list.
  // See: https://checkout.atlassian.net/wiki/spaces/OA/pages/7110853250/Client+Entity+Onboarding+on+Dashboard+Core
  const activeEntity = client?.entities?.find(
    (entity) => entity?.status === ACTIVE_DASHBOARD_STATE,
  );
  const defaultEntity = activeEntity ?? client?.entities?.[0];
  const entity = selectedEntity ?? defaultEntity;

  const entityHasOnboardingStatus = ENTITY_ONBOARDING_STATES.includes(entity?.status ?? '');

  if (isApplierRoleEnabled) {
    return entityHasOnboardingStatus;
  }

  return entityHasOnboardingStatus && !!tokenProperties?.userRoles?.includes(ONBOARDING_ROLE);
};
